import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CONVERSION_ERROR,
  EXAMPLE_DATASET,
  getTenant,
  updateTenant,
  type Tenant,
  type TenantDeletion,
  type TenantDemoConversion,
} from "../platform/tenantsApi";
import { copyToClipboard, monogram } from "../platform/tenantIdentity";
import { TenantProvisioningSection } from "../platform/TenantProvisioningSection";
import { TenantPrincipalSection } from "../platform/TenantPrincipalSection";
import { TenantRolesSection } from "../platform/TenantRolesSection";
import { TenantFreezeSection } from "../platform/TenantFreezeSection";
import { TenantDemoSection } from "../platform/TenantDemoSection";
import { TenantShredOutstanding } from "../platform/TenantShredOutstanding";
import { TenantDeleteSection } from "../platform/TenantDeleteSection";
import { conversionBannerKey, needsShredRetry } from "../platform/tenantDemoConversion";
import { deleteSectionState } from "../platform/tenantDeletion";
import type { TenantProvisioning } from "../platform/provisioningApi";
import { ACTIVATION_AWAITING_ACTIVATION } from "../platform/provisioningSteps";
import { accessBadgeKey } from "../platform/tenantAccess";
import {
  CLASS_III_MASTERS,
  CLASS_III_MASTER_TENANT_ADMIN,
  classIiiMasterLabelKey,
} from "@web/admin/classIiiMaster";
import { ApiError, SessionExpiredError } from "@web/lib/api";
import { formatDate } from "@web/lib/date";
import { Card } from "@web/ui";
import { PAGE_TITLE_ID, usePageTitle } from "@web/app/pageTitle";

// The tenant's OWN page (`/tenants/:tenantId`) — every act the operator can perform on one customer, on
// one surface. Designer handoff:
// `docs/product/alustakonsoli/tenanttihallinta/design_handoff_tenant_page/README.md`.
//
// WHY A PAGE AND NOT FIVE DIALOGS. The console used to do all of this from a row menu on `/tenants`:
// edit, provisioning, the pääkäyttäjyys handover — three dialogs over a directory, with the tenant's
// identity repeated inside each one. Five actions behind a kebab is a surface where the operator cannot
// see what is true of a customer without opening things one at a time, and where two of those things
// (which steps are left, who holds pääkäyttäjyys) are exactly the facts they came to find out. So the
// list became a DIRECTORY — a row opens the tenant — and the actions became SECTIONS of the tenant.
//
// ANCHORS, NOT TABS (handoff §2, decided). Tabs were built in the prototype and rejected: the sections
// are facts about one customer, and a tab bar makes all but one of them invisible and unprintable. The
// side nav scrolls to a section and says what state each one is in, so the answer to "is this customer
// onboarded, open, whose is it and who from Incedo reaches it" is on screen before anything is clicked.
//
// WHAT IS READ WHEN, AND WHY THAT IS NOT AN IMPLEMENTATION DETAIL. Four of this page's reads write audit
// rows (FR-Q.35), and they are treated differently on purpose:
//
//   * `GET …/tenants/{id}` — the tenant read this page is built on — is one of them: reading one NAMED
//     tenant across the isolation boundary is the crossing FR-Q.35 records (`TENANT_ACCESSED`,
//     `{"viewed":"tenant_profile"}`). It runs on load, which is the point — one row per page open, and the
//     open is the operator's explicit choice of that tenant. What must not happen is a row for a tenant
//     nobody opened, which is why the DIRECTORY stays on the unaudited list read.
//   * `GET …/provisioning` IS read on load (product-owner decision, 6.10.2026), for the same reason — and
//     provisioning is the page's first section, so the alternative was a section that sits empty until it
//     is clicked.
//   * `GET …/principal-candidates` is NOT. Choosing a tenant is not choosing to look at who works there,
//     and that read discloses the customer's PEOPLE. It runs when the handover is opened, and the closed
//     section says so in words.
//   * `GET …/roles` (ADR-0018 §7's holder list) is NOT either, for exactly that reason and one more: the
//     people it discloses include Incedo's own, and `{"viewed":"tenant_role_holders"}` is the row that
//     says somebody went looking at who reaches this customer. It runs when that section is opened.
//
// An e2e guard watches the network for all four (the FR-Q.35 AUDIT GUARD in
// `apps/platform/e2e/platform-tenant.realstack.spec.ts`), so the split above is held by a test and not by
// this comment.
//
// A 404 IS NOT AN ERROR HERE. It is also the answer for the operator's OWN tenant (ADR-0014 §12 A-2),
// which is not a customer — so the not-found state links back to the directory rather than inviting a
// retry of something that will never succeed.

/** The page's own load. A 404 is its own state: see above. */
type Load =
  | { phase: "loading" }
  | { phase: "notFound" }
  | { phase: "error" }
  | { phase: "ready"; tenant: Tenant };

/**
 * The five sections, in page order. The ids are both the anchor targets and the nav's keys.
 *
 * `labelKey` is the SECTION'S OWN title key — the same string each section renders as its <h2>, so the
 * nav entry and the heading it scrolls to can never drift apart. It is spelled out rather than built
 * from the id because `src/i18n/platform-i18n.test.mjs` reads this file for key literals, and a key
 * assembled at runtime is one it cannot check against both locales.
 */
const SECTIONS = [
  { id: "provisioning", labelKey: "tenants.provisioning.sectionTitle" },
  { id: "settings", labelKey: "tenants.settings.title" },
  { id: "principal", labelKey: "tenants.principal.sectionTitle" },
  // ADR-0018 §7 — the vastuukonsultti / HR grant surface. Directly after pääkäyttäjyys, because the two
  // are the same kind of question (who holds an authority in this customer) and the operator reaching for
  // one has to see the other: the ADMIN seat is NOT granted here and §3.3's refusal says so by naming
  // the section above.
  { id: "roles", labelKey: "tenants.roles.sectionTitle" },
  { id: "freeze", labelKey: "tenants.freeze.title" },
  // ADR-0023 — the one act on this console that cannot be undone, LAST and directly after the freeze it
  // requires. The section renders nothing at all for a tenant that is not a demo tenant, so the nav entry
  // is filtered out with it (`visibleSections`): a nav row pointing at an element that does not exist would
  // scroll nowhere and would advertise an operation this console does not have for a customer.
  { id: "delete", labelKey: "tenants.delete.title" },
] as const;

type SectionId = (typeof SECTIONS)[number]["id"];

/** The DOM id of a section, which is also what `#hash` and the nav links address. */
function sectionId(section: SectionId): string {
  return `tenant-section-${section}`;
}

/** The id of a section's heading, so each section's region carries its own accessible name. */
function sectionHeadingId(section: SectionId): string {
  return `tenant-heading-${section}`;
}

/** The tone of a nav entry's status dot. Good = settled, critical = shut, neutral = unfinished/unknown. */
type Tone = "good" | "neutral" | "critical";

export function TenantPage() {
  const { t } = useTranslation();
  const { tenantId = "" } = useParams<{ tenantId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [load, setLoad] = useState<Load>({ phase: "loading" });
  const [banner, setBanner] = useState<string | null>(null);
  /**
   * Where provisioning stands, for the nav's status line. Owned here and reported UP by the section,
   * rather than read twice: the read is audited, so a second caller would be a second audit row.
   */
  const [provisioning, setProvisioning] = useState<TenantProvisioning | null>(null);
  /** Who holds pääkäyttäjyys — `undefined` until the handover has been opened (see the header comment). */
  const [holder, setHolder] = useState<string | null | undefined>(undefined);
  /**
   * How many vastuukonsultit / HR experts this tenant has (ADR-0018 §7). `undefined` until the section
   * has been opened, for the reason `holder` is — that read is audited and discloses the customer's
   * people, and the nav is not a reason to perform it. `null` is the other thing the nav must be able to
   * say: the read happened and did NOT establish a count (a 502 from the realm), which is not zero.
   */
  const [roleHolders, setRoleHolders] = useState<number | null | undefined>(undefined);
  /**
   * Task e03c428d — a conversion (or a retry of one) that left Transit keys unconfirmed, kept HERE rather
   * than in the section the operator pressed the button in. That section exists only while `tenant.isDemo`,
   * and a conversion that reached this state has already turned the mark off: holding it there meant the
   * re-read below unmounted the outstanding count and the retry together with it, leaving a banner that
   * reported a shred nobody had confirmed. Null whenever nothing is owed, which is every other outcome.
   */
  const [shredOutstanding, setShredOutstanding] = useState<TenantDemoConversion | null>(null);
  const [activeSection, setActiveSection] = useState<SectionId>("provisioning");

  const tenant = load.phase === "ready" ? load.tenant : null;

  /**
   * The access facts — step 3's answer and when it was last given — from the provisioning read once it has
   * landed, and from the DIRECTORY read until then (task aa0c3eb7).
   *
   * Both are the server's, and the fallback is what changed: the tenant row carries these two fields since
   * this task, so the header and the nav no longer have to withhold the state until the audited status read
   * answers. They still PREFER the provisioning read, because it is the one a step-3 save returns — the
   * tenant re-read runs beside it and either may land first.
   */
  const access = provisioning
    ? { entitlements: provisioning.entitlements, recordedAt: provisioning.entitlementsRecordedAt }
    : { entitlements: tenant?.entitlements, recordedAt: tenant?.entitlementsRecordedAt };

  const reload = useCallback(
    (signal?: AbortSignal) => {
      getTenant(tenantId, signal)
        .then((fresh) => setLoad({ phase: "ready", tenant: fresh }))
        .catch((err: unknown) => {
          if (signal?.aborted || err instanceof SessionExpiredError) return;
          // A failed REFRESH leaves the page standing. This read also runs after every mutation, and the
          // tenant on screen at that moment is the one the server just returned from the write — blanking
          // it because a follow-up read dropped would throw away the newer fact to report the older
          // failure. Only the FIRST read, with nothing on screen yet, can turn this page into an error.
          setLoad((previous) =>
            previous.phase === "ready"
              ? previous
              : { phase: err instanceof ApiError && err.status === 404 ? "notFound" : "error" },
          );
        });
    },
    [tenantId],
  );

  useEffect(() => {
    const controller = new AbortController();
    setLoad({ phase: "loading" });
    reload(controller.signal);
    return () => controller.abort();
  }, [reload]);

  // The create on `/tenants` navigates HERE, so its "Tenant X luotu" banner has to be said on this page:
  // the list the operator left is not where the tenant now is. The flag is dropped from the history entry
  // once it has been read, so a reload of this URL does not re-announce a create that already happened.
  const created = (location.state as { created?: boolean } | null)?.created === true;
  useEffect(() => {
    if (!created || !tenant) return;
    setBanner(t("tenants.created", { tenant: tenant.slug }));
    navigate(location.pathname, { replace: true, state: null });
  }, [created, tenant, location.pathname, navigate, t]);

  // The top bar's <h1> is the SLUG — the only name this console has for a customer (ADR-0014 §7.3). The
  // route's static handle ("Alustan hallinta") stands while the read is in flight, which is the page-title
  // channel's own loading answer; this overrides it the moment the tenant lands.
  usePageTitle(tenant?.slug);

  // Which section the page is looking at, for the nav's `aria-current`. An observer and not a scroll
  // handler: the browser computes the intersections, so this costs nothing while the operator reads.
  //
  // Keyed on whether the sections EXIST, not on the tenant: the tenant object is replaced by every
  // re-read, and an observer torn down and rebuilt on each save would be churn for a DOM that did not
  // move.
  const sectionsMounted = load.phase === "ready";
  useEffect(() => {
    if (!sectionsMounted) return;
    const elements = SECTIONS.map((section) => document.getElementById(sectionId(section.id))).filter(
      (element): element is HTMLElement => element !== null,
    );
    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // The topmost section that is on screen wins. Taking the "most visible" one instead makes the
        // nav flicker between two neighbours while a long section scrolls past.
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        const top = visible[0]?.target.id;
        const found = SECTIONS.find((section) => sectionId(section.id) === top);
        if (found) setActiveSection(found.id);
      },
      // A top margin the height of the sticky bar, so a section that has scrolled under the chrome is no
      // longer "the one being read".
      { rootMargin: "-20% 0px -60% 0px" },
    );
    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [sectionsMounted]);

  /** A section's state line in the nav: its text and the tone of its dot. */
  const navStatus = useCallback(
    (section: SectionId): { text: string; tone: Tone } => {
      if (!tenant) return { text: t("tenants.nav.unknown"), tone: "neutral" };
      switch (section) {
        case "provisioning":
          if (!provisioning) return { text: t("tenants.nav.unknown"), tone: "neutral" };
          // ACCESS BEATS PROGRESS (task aa0c3eb7). A customer tenant with no `clms` entitlement is served
          // nothing at all, and "Kesken" is the line that has been saying so — a neutral dot on a step
          // nobody has finished, for a customer who cannot sign in. The nav says the consequence and wears
          // the critical dot the freeze's entry wears, because it is the same consequence; which step is
          // outstanding is still on the section this entry scrolls to.
          //
          // Which of the two shut states it is comes from `accessBadgeKey`, so the nav, the header and the
          // directory cannot call the same state by different names.
          {
            const shutKey = accessBadgeKey(access.entitlements, access.recordedAt);
            if (shutKey) return { text: t(shutKey), tone: "critical" };
          }
          if (!provisioning.complete) {
            return { text: t("tenants.provisioning.state.pending"), tone: "neutral" };
          }
          // Every step that can be done is done AND the invited administrator has not followed their
          // activation link (ADR-0014 §7.5.3). A "Tehty" dot here would be the one line on this page that
          // claims an accepted onboarding, which is exactly what is outstanding — and the operator's own
          // remedy (resend, or cancel and name somebody else) is in the section this entry scrolls to.
          return provisioning.firstAdministrator?.activation === ACTIVATION_AWAITING_ACTIVATION
            ? { text: t("tenants.provisioning.identity.activation.awaiting_activation"), tone: "neutral" }
            : { text: t("tenants.provisioning.state.done"), tone: "good" };
        case "settings":
          return {
            text: tenant.allowsExternalLearners
              ? t("tenants.externalAllowed")
              : t("tenants.externalNone"),
            tone: "neutral",
          };
        case "principal":
          // `undefined` means the audited candidate read has not happened — and the nav is not a reason
          // to make it happen, so it says it does not know rather than guessing.
          if (holder === undefined) return { text: t("tenants.nav.unknown"), tone: "neutral" };
          return holder === null
            ? { text: t("tenants.principal.navNoHolder"), tone: "neutral" }
            : { text: holder, tone: "good" };
        case "roles":
          // Three states and not two. `undefined` is "not read" — the audited read has not happened and
          // the nav is not a reason to make it happen. `null` is "read, and the realm would not answer",
          // which must NOT read as "nobody": that is the claim ADR-0016 §4.7 point 4 forbids this surface
          // from making. Only a number is a number.
          if (roleHolders === undefined) return { text: t("tenants.nav.unknown"), tone: "neutral" };
          if (roleHolders === null) return { text: t("tenants.roles.navUnknown"), tone: "neutral" };
          return roleHolders === 0
            ? { text: t("tenants.roles.navNoHolders"), tone: "neutral" }
            : { text: t("tenants.roles.navHolders", { count: roleHolders }), tone: "good" };
        case "freeze":
          return tenant.suspendedAt === null
            ? { text: t("tenants.statusActive"), tone: "good" }
            : { text: t("tenants.statusFrozen"), tone: "critical" };
        case "delete":
          // Only ever asked for a demo tenant — `visibleSections` drops this entry for the rest — and the
          // two it can answer are the section's own two states. A CRITICAL dot on the available one: this
          // entry's own colour is the last warning before an irreversible act, and the freeze above wears
          // the same dot for a lesser consequence.
          return deleteSectionState(tenant) === "ready"
            ? { text: t("tenants.delete.navReady"), tone: "critical" }
            : { text: t("tenants.delete.navNeedsFreeze"), tone: "neutral" };
      }
    },
    [tenant, provisioning, access.entitlements, access.recordedAt, holder, roleHolders, t],
  );

  // A step landed / a setting was saved: re-read the tenant so the header badges and the nav agree with
  // the server rather than with the read the page opened on.
  const onChanged = useCallback(() => reload(), [reload]);

  /**
   * Task e03c428d — ONE place turns a conversion's answer into what the operator sees, and both callers
   * use it: the conversion itself and the shred retry that finishes an incomplete one. That is what stops
   * the retry from being able to report a completeness the conversion would not have.
   *
   * It says the outcome's own sentence, keeps the result when keys are still owed — read off the COUNT and
   * not only off the outcome, so a server that claimed `converted` beside a non-zero count still gets the
   * retry (ADR-0013) — and re-reads the tenant, because by now the demo mark is off and the header, the
   * nav and the settings section are all describing a tenant that no longer exists in that shape.
   */
  const onConversion = useCallback(
    (result: TenantDemoConversion) => {
      setBanner(t(conversionBannerKey(result.outcome), { tenant: tenant?.slug ?? "" }));
      setShredOutstanding(needsShredRetry(result) ? result : null);
      reload();
    },
    [t, tenant?.slug, reload],
  );

  /**
   * ADR-0023 — the tenant is gone, so this page is gone with it: it is built on a read that now answers
   * 404, and every section below describes rows that no longer exist. The operator goes back to the
   * directory with the outcome's own sentence, and that is also where the deletion's unfinished business
   * lives (`TenantDeletionsSection`) — the normal successful deletion owes one `keycloak_federation` row
   * for about one provisioner pass, and the retry for anything else has to survive this page.
   *
   * `replace`, so the browser's back button does not return to a page that can only 404.
   */
  const onDeleted = useCallback(
    (result: TenantDeletion) => {
      navigate("/tenants", { replace: true, state: { deleted: result } });
    },
    [navigate],
  );

  const onLifecycle = useCallback(
    (saved: Tenant, what: "suspended" | "resumed") => {
      setLoad({ phase: "ready", tenant: saved });
      setBanner(
        t(what === "suspended" ? "tenants.freeze.done" : "tenants.freeze.undone", {
          tenant: saved.slug,
        }),
      );
    },
    [t],
  );

  if (load.phase === "loading") {
    return (
      <section className="tenant-page" aria-labelledby={PAGE_TITLE_ID}>
        <p className="empty-state" role="status" aria-live="polite">
          {t("tenants.loading")}
        </p>
      </section>
    );
  }

  if (load.phase === "notFound" || load.phase === "error") {
    return (
      <section className="tenant-page" aria-labelledby={PAGE_TITLE_ID}>
        <Breadcrumb />
        <Card>
          <p
            className={load.phase === "notFound" ? "empty-state" : "banner banner--error"}
            role={load.phase === "notFound" ? undefined : "alert"}
            data-testid={load.phase === "notFound" ? "tenant-not-found" : "tenant-load-error"}
          >
            {t(load.phase === "notFound" ? "tenants.notFound" : "tenants.loadError")}
          </p>
          <p>
            <Link to="/tenants">{t("tenants.backToList")}</Link>
          </p>
        </Card>
      </section>
    );
  }

  const frozen = load.tenant.suspendedAt !== null;

  // ADR-0023 — the deletion section renders NOTHING for a tenant that is not a demo tenant (deleting a
  // customer's tenant is not an operation this console has), so its nav entry goes with it rather than
  // pointing at an element that is not on the page.
  const visibleSections = SECTIONS.filter(
    (section) => section.id !== "delete" || deleteSectionState(load.tenant) !== "none",
  );

  return (
    <section className="tenant-page" aria-labelledby={PAGE_TITLE_ID} data-testid="tenant-page">
      <Breadcrumb slug={load.tenant.slug} />

      <TenantIdentityHeader
        tenant={load.tenant}
        provisioningComplete={provisioning?.complete ?? load.tenant.provisioningComplete}
        // Which shut state this customer is in, as an i18n key, or null when it is being served (task
        // aa0c3eb7). No longer withheld until the audited provisioning read lands: the directory read
        // carries the same two facts now, so the header can say the thing an operator opened the page to
        // find out from its first paint.
        accessBadgeKey={accessBadgeKey(access.entitlements, access.recordedAt)}
        onCopied={() => setBanner(t("tenants.uuidCopied"))}
      />

      {banner && (
        <p className="banner banner--success" role="status">
          {banner}
        </p>
      )}

      {/* Task e03c428d — an `incomplete` conversion's outstanding Transit keys and the action that
          finishes them, HERE and not in the demo section the operator pressed the button in: that section
          is gone by now, because the conversion turned the demo mark off. See TenantShredOutstanding. */}
      {shredOutstanding && (
        <TenantShredOutstanding
          tenant={load.tenant}
          result={shredOutstanding}
          onRetried={onConversion}
        />
      )}

      {/* The frozen state is stated ABOVE the sections and not only as a pill: every form below it
          behaves differently, and an operator who missed a pill would read a half-disabled page as broken. */}
      {frozen && (
        <p className="banner banner--error" role="status" data-testid="tenant-frozen-banner">
          {t("tenants.freeze.frozenBanner", {
            date: formatDate(new Date(load.tenant.suspendedAt!)),
            reason: load.tenant.suspensionReason ?? t("tenants.freeze.noReason"),
          })}
        </p>
      )}

      <div className="tenant-page__body">
        <SectionNav
          sections={visibleSections}
          active={activeSection}
          status={navStatus}
          onNavigate={setActiveSection}
        />

        <div className="tenant-page__sections">
          <Section id="provisioning">
            <TenantProvisioningSection
              tenant={load.tenant}
              headingId={sectionHeadingId("provisioning")}
              onChanged={onChanged}
              onStatus={setProvisioning}
            />
          </Section>

          <Section id="settings">
            <TenantSettingsSection
              tenant={load.tenant}
              headingId={sectionHeadingId("settings")}
              onSaved={(saved) => {
                setLoad({ phase: "ready", tenant: saved });
                setBanner(t("tenants.updated", { tenant: saved.slug }));
              }}
              onEditing={() => setBanner(null)}
              // Task e03c428d — the conversion ran; `onConversion` says the outcome, re-reads the tenant
              // and keeps an unfinished shred alive above, outside the block that is about to unmount.
              onConverted={onConversion}
            />
          </Section>

          <Section id="principal">
            <TenantPrincipalSection
              tenant={load.tenant}
              headingId={sectionHeadingId("principal")}
              onHolder={setHolder}
              onDone={(transferred) =>
                setBanner(
                  t("tenants.principal.done", {
                    from: transferred.from.name,
                    to: transferred.to.name,
                    tenant: load.tenant.slug,
                  }),
                )
              }
            />
          </Section>

          <Section id="roles">
            <TenantRolesSection
              tenant={load.tenant}
              headingId={sectionHeadingId("roles")}
              onHolders={setRoleHolders}
              onDone={setBanner}
            />
          </Section>

          <Section id="freeze" critical>
            <TenantFreezeSection
              tenant={load.tenant}
              headingId={sectionHeadingId("freeze")}
              onChanged={onLifecycle}
            />
          </Section>

          {/* ADR-0023 — directly after the freeze it requires, and in a section of its own rather than
              inside it: the freeze deletes nothing and offers a thaw, and this is the one act on this
              console that cannot be undone. It draws nothing for a tenant that is not a demo tenant, which
              is why the <Section> wrapper comes with it rather than standing empty. */}
          {deleteSectionState(load.tenant) !== "none" && (
            <Section id="delete" critical>
              <TenantDeleteSection
                tenant={load.tenant}
                headingId={sectionHeadingId("delete")}
                onDeleted={onDeleted}
              />
            </Section>
          )}
        </div>
      </div>
    </section>
  );
}

/** "Tenantit / slug" — the way back, and the only place this page repeats where it sits. */
function Breadcrumb({ slug }: { slug?: string }) {
  const { t } = useTranslation();
  return (
    <nav className="tenant-page__crumbs" aria-label={t("tenants.breadcrumbLabel")}>
      <Link to="/tenants">{t("tenants.title")}</Link>
      {slug && (
        <>
          <span aria-hidden="true"> / </span>
          <span className="tenant-page__crumb-current">{slug}</span>
        </>
      )}
    </nav>
  );
}

/** One section: a card that is also an anchor target and a labelled region. */
function Section({
  id,
  critical = false,
  children,
}: {
  id: SectionId;
  /** The freeze section wears the critical edge — it is the one that shuts a customer down. */
  critical?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section
      id={sectionId(id)}
      className={`card tenant-section${critical ? " tenant-section--critical" : ""}`}
      aria-labelledby={sectionHeadingId(id)}
      data-testid={sectionId(id)}
    >
      {children}
    </section>
  );
}

/**
 * The in-page section nav (handoff §2, "ankkurit"). Real links to real fragments, so the keyboard, the
 * middle click and the browser's own back all work — the click handler only replaces the instant jump
 * with a scroll, and steps aside under `prefers-reduced-motion`.
 */
function SectionNav({
  sections,
  active,
  status,
  onNavigate,
}: {
  /**
   * The sections this tenant actually HAS, which is not always all five: ADR-0023's deletion section draws
   * nothing for a tenant that is not a demo tenant, and an anchor to a missing element would scroll
   * nowhere while advertising an operation the console does not offer for a customer.
   */
  sections: readonly (typeof SECTIONS)[number][];
  active: SectionId;
  status: (section: SectionId) => { text: string; tone: Tone };
  onNavigate: (section: SectionId) => void;
}) {
  const { t } = useTranslation();

  return (
    <nav className="tenant-nav" aria-label={t("tenants.nav.label")} data-testid="tenant-nav">
      <ul>
        {sections.map(({ id, labelKey }) => {
          const { text, tone } = status(id);
          return (
            <li key={id}>
              <a
                href={`#${sectionId(id)}`}
                aria-current={id === active ? "true" : undefined}
                data-testid={`tenant-nav-${id}`}
                onClick={(e) => {
                  const target = document.getElementById(sectionId(id));
                  if (!target) return; // no element to scroll to — let the browser do its default
                  e.preventDefault();
                  const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
                  target.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" });
                  // The hash follows so the section is linkable, but WITHOUT a history entry: the nav is
                  // a view of one page, and four back-presses to leave it would be the tab bar's problem
                  // wearing a different shape.
                  window.history.replaceState(null, "", `#${sectionId(id)}`);
                  onNavigate(id);
                }}
              >
                <span className="tenant-nav__label">{t(labelKey)}</span>
                <span className="tenant-nav__state">
                  <span className="tenant-nav__dot" data-tone={tone} aria-hidden="true" />
                  {text}
                </span>
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/**
 * ALUSTAN ASETUKSET — the platform-owned half of a tenant's settings (ADR-0014 §7.3), edited IN PLACE.
 *
 * In place and not in a dialog, which is the one change of substance from the old `TenantDialog` edit
 * mode: the section already shows the three values, so a dialog would re-draw them a second time over the
 * page that has them. The call and every error message are the same.
 *
 * The NAME and the LOGO are not here and their absence is the decision (§7.3): both are the tenant's own,
 * in a table this console cannot read or write. The note says so where an operator would reach for them,
 * rather than leaving them merely missing.
 */
function TenantSettingsSection({
  tenant,
  headingId,
  onSaved,
  onEditing,
  onConverted,
}: {
  tenant: Tenant;
  headingId: string;
  onSaved: (saved: Tenant) => void;
  /** The operator started an edit — the page clears whatever banner the last action left. */
  onEditing: () => void;
  /** The demo → customer conversion ran (task e03c428d); the page re-reads and says what happened. */
  onConverted: (result: TenantDemoConversion) => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [allowsExternal, setAllowsExternal] = useState(tenant.allowsExternalLearners);
  const [classIiiMaster, setClassIiiMaster] = useState(tenant.classIiiMaster);
  const [isDemo, setIsDemo] = useState(tenant.isDemo);
  const [mcpEnabled, setMcpEnabled] = useState(tenant.mcpEnabled);
  const [submitting, setSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const editRef = useRef<HTMLButtonElement>(null);
  const firstFieldRef = useRef<HTMLInputElement>(null);

  const onOpen = useCallback(() => {
    // Seeded from the tenant each time the form opens, so a cancelled edit cannot leave a stale draft
    // behind for the next one.
    setAllowsExternal(tenant.allowsExternalLearners);
    setClassIiiMaster(tenant.classIiiMaster);
    setIsDemo(tenant.isDemo);
    setMcpEnabled(tenant.mcpEnabled);
    setFieldError(null);
    setEditing(true);
    onEditing();
    queueMicrotask(() => firstFieldRef.current?.focus());
  }, [tenant, onEditing]);

  const onCancel = useCallback(() => {
    setEditing(false);
    setFieldError(null);
    queueMicrotask(() => editRef.current?.focus());
  }, []);

  const onSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (submitting) return;
      setFieldError(null);
      setSubmitting(true);
      updateTenant(tenant.id, {
        allowsExternalLearners: allowsExternal,
        classIiiMaster,
        isDemo,
        mcpEnabled,
      })
        .then((saved) => {
          setEditing(false);
          onSaved(saved);
          queueMicrotask(() => editRef.current?.focus());
        })
        .catch((err: unknown) => {
          if (err instanceof SessionExpiredError) return;
          // Task e03c428d — the ONE state that refuses this save: unticking Demotenantti over a tenant
          // that still holds the example dataset. NOTHING of the form was saved, and the remedy is not a
          // corrected field but the conversion button below, so the message says so instead of reading as
          // a transport failure. Branched on the problem's `error` and not on the 409, because this form
          // has no other 409 today and inventing one later must not silently inherit this sentence.
          setFieldError(
            err instanceof ApiError
              ? t(
                  err.problem?.error === CONVERSION_ERROR.datasetPresent
                    ? "tenants.demo.unmarkRefused"
                    : err.status === 422
                      ? "tenants.validationError"
                      : "tenants.saveError",
                )
              : t("tenants.saveError"),
          );
        })
        .finally(() => setSubmitting(false));
    },
    [submitting, tenant.id, allowsExternal, classIiiMaster, isDemo, mcpEnabled, onSaved, t],
  );

  return (
    <>
      <div className="tenant-section__head">
        <h2 id={headingId}>{t("tenants.settings.title")}</h2>
        {!editing && (
          <button
            type="button"
            className="btn"
            ref={editRef}
            onClick={onOpen}
            data-testid="tenant-settings-edit"
          >
            {t("tenants.edit")}
          </button>
        )}
      </div>

      <p className="tenant-section__note" role="note" data-testid="tenant-owned-note">
        {t("tenants.tenantOwnedNote")}
      </p>

      {editing ? (
        <form className="form" onSubmit={onSubmit} aria-labelledby={headingId}>
          <label className="tenants__check">
            <input
              type="checkbox"
              ref={firstFieldRef}
              checked={allowsExternal}
              onChange={(e) => setAllowsExternal(e.target.checked)}
            />
            {t("tenants.allowsExternal")}
          </label>

          {/* ADR-0014 §3 / §7.3 — a PLATFORM-owned setting, which is why it is on this console and on none
              of the tenant's own. Choosing anything but the administrator makes the class-III fields
              read-only on every surface inside that tenant, so the hint says so before the operator
              commits to it. */}
          <label htmlFor="tenant-class-iii">{t("tenants.classIiiMaster")}</label>
          <select
            id="tenant-class-iii"
            value={classIiiMaster}
            onChange={(e) => setClassIiiMaster(e.target.value)}
            aria-describedby="tenant-class-iii-hint"
            data-testid="tenant-class-iii"
          >
            {CLASS_III_MASTERS.map((code) => (
              <option key={code} value={code}>
                {t(classIiiMasterLabelKey(code))}
              </option>
            ))}
          </select>
          <p id="tenant-class-iii-hint" className="tenants__slug-status">
            {classIiiMaster === CLASS_III_MASTER_TENANT_ADMIN
              ? t("tenants.classIiiHintAdmin")
              : t("tenants.classIiiHintMastered")}
          </p>

          {/* Task efc66b0b — the demo mark. It is the OPERATOR's classification of the customer and the
              whole of what makes the example-dataset card exist for that tenant's own administrator, so
              the hint says what ticking it hands over before the operator commits to it. The box is here
              and on no tenant-facing surface for the structural reason the toggle above is: `tenants` is
              the one table outside the tenant query filter and ADR-0011 §4's RLS. */}
          <label className="tenants__check">
            <input
              type="checkbox"
              checked={isDemo}
              onChange={(e) => setIsDemo(e.target.checked)}
              aria-describedby="tenant-is-demo-hint"
              data-testid="tenant-is-demo"
            />
            {t("tenants.isDemo")}
          </label>
          <p id="tenant-is-demo-hint" className="tenants__slug-status">
            {isDemo ? t("tenants.isDemoHintOn") : t("tenants.isDemoHintOff")}
          </p>
          {/* Task e03c428d — the refusal said BEFORE the save rather than after it. Unticking this box
              over a tenant that still holds the example dataset is refused by the server (nothing is
              saved, not even the three settings above), because the dataset would be stranded: the
              example-dataset routes answer 404 for a tenant that is not a demo tenant. The remedy is the
              conversion button under this form, and an operator who reads it here does not have to meet
              the 409 to find out. */}
          {tenant.isDemo && !isDemo && tenant.exampleDataset !== EXAMPLE_DATASET.none && (
            <p
              className="field-error"
              role="status"
              data-testid="tenant-is-demo-untick-warning"
            >
              {t("tenants.demo.unmarkRefused")}
            </p>
          )}

          {/* ADR-0019 §4 — the MCP switch. It is here and on no tenant-facing surface for the same
              structural reason as the two boxes above (`tenants` is outside the tenant query filter and
              ADR-0011 §4's RLS), and for one of its own: §11's legal questions — controller and processor,
              whether the processing agreement must name the AI provider, whether the customer must consent
              rather than merely see it — are unanswered, and the answer is Verso's to record. The tenant's
              own pääkäyttäjä can READ it, which is why the hint says what the customer will be able to
              see. */}
          <label className="tenants__check">
            <input
              type="checkbox"
              checked={mcpEnabled}
              onChange={(e) => setMcpEnabled(e.target.checked)}
              aria-describedby="tenant-mcp-enabled-hint"
              data-testid="tenant-mcp-enabled"
            />
            {t("tenants.mcpEnabled")}
          </label>
          <p id="tenant-mcp-enabled-hint" className="tenants__slug-status">
            {mcpEnabled ? t("tenants.mcpEnabledHintOn") : t("tenants.mcpEnabledHintOff")}
          </p>

          {fieldError && (
            <p className="field-error" role="alert" data-testid="tenant-settings-error">
              {fieldError}
            </p>
          )}

          <div className="tenant-section__actions">
            <button type="button" className="btn" onClick={onCancel} disabled={submitting}>
              {t("tenants.cancel")}
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={submitting}
              data-testid="tenant-edit-submit"
            >
              {submitting ? t("tenants.saving") : t("tenants.save")}
            </button>
          </div>
        </form>
      ) : (
        <dl className="tenant-facts" data-testid="tenant-settings-view">
          <dt>{t("tenants.slug")}</dt>
          <dd>
            <code className="tenants__slug">{tenant.slug}</code>{" "}
            <span className="tenant-facts__aside">· {t("tenants.slugImmutable")}</span>
          </dd>

          <dt>{t("tenants.colExternal")}</dt>
          <dd>
            {tenant.allowsExternalLearners ? (
              <span className="badge badge--external-open" data-testid="tenant-external">
                {t("tenants.externalAllowed")}
              </span>
            ) : (
              <span className="badge badge--status-not_started" data-testid="tenant-external">
                {t("tenants.externalNone")}
              </span>
            )}
          </dd>

          <dt>{t("tenants.classIiiMaster")}</dt>
          <dd data-testid="tenant-class-iii-value">
            {t(classIiiMasterLabelKey(tenant.classIiiMaster))}
          </dd>

          <dt>{t("tenants.isDemo")}</dt>
          <dd>
            {tenant.isDemo ? (
              <span className="badge badge--external-open" data-testid="tenant-is-demo-value">
                {t("tenants.isDemoYes")}
              </span>
            ) : (
              <span className="badge badge--status-not_started" data-testid="tenant-is-demo-value">
                {t("tenants.isDemoNo")}
              </span>
            )}
          </dd>

          <dt>{t("tenants.mcpEnabled")}</dt>
          <dd>
            {tenant.mcpEnabled ? (
              <span className="badge badge--external-open" data-testid="tenant-mcp-enabled-value">
                {t("tenants.mcpEnabledYes")}
              </span>
            ) : (
              <span className="badge badge--status-not_started" data-testid="tenant-mcp-enabled-value">
                {t("tenants.mcpEnabledNo")}
              </span>
            )}
          </dd>
        </dl>
      )}

      {/* Task e03c428d — the demo mark's other half, and the reason it sits inside this section rather
          than in one of its own: it is what the checkbox above cannot do on its own. Rendered in both the
          edit and the view state, because the operator reaching for the checkbox is exactly the operator
          who needs it. Nothing at all for a tenant that is not a demo tenant. */}
      {tenant.isDemo && <TenantDemoSection tenant={tenant} onConverted={onConverted} />}
    </>
  );
}

/**
 * The identity header: who this page is about, and the one value the operator came here to copy.
 *
 * ADR-0013 §5 — creating a tenant creates NOTHING in Keycloak, and onboarding it is two attributes on the
 * intended first administrator, `tenant_id` being exactly this UUID. The operator needs the raw value, so
 * it is shown verbatim and selectable with a copy button beside it. It used to be on every row of the
 * directory; it belongs here, on the one tenant the operator has actually opened.
 */
function TenantIdentityHeader({
  tenant,
  provisioningComplete,
  accessBadgeKey: shutKey,
  onCopied,
}: {
  tenant: Tenant;
  /** The server's latest word, from the provisioning read when it has landed. */
  provisioningComplete: boolean;
  /**
   * The customer is served nothing — no `clms` entitlement, so every `/api` and `/scorm` request of theirs
   * is refused — as the i18n key naming WHICH shut state it is, or null for a served customer (task
   * aa0c3eb7). Its own badge beside the freeze's, because it is the same kind of fact: whether this
   * customer is being served. "Provisiointi kesken" was all the header said about it, which reads as
   * paperwork rather than as a shut customer.
   *
   * The key and not the booleans behind it, so the header renders the state and does not re-decide it:
   * `platform/tenantAccess.ts` is where that reading lives, for the directory and the nav as well.
   */
  accessBadgeKey: string | null;
  onCopied: () => void;
}) {
  const { t } = useTranslation();
  const frozen = tenant.suspendedAt !== null;

  return (
    <Card className="tenant-identity">
      <span className="tenants__monogram tenant-identity__monogram" aria-hidden="true">
        {monogram(tenant.slug)}
      </span>
      <div className="tenant-identity__main">
        <div className="tenant-identity__line">
          <code className="tenant-identity__slug">{tenant.slug}</code>
          {frozen ? (
            <span className="badge badge--tenant-frozen" data-testid="tenant-status">
              {t("tenants.statusFrozen")}
            </span>
          ) : (
            <span className="badge badge--user-active" data-testid="tenant-status">
              {t("tenants.statusActive")}
            </span>
          )}
          {/* "Provisiointi kesken" means a STEP is outstanding, and an invite awaiting activation is not
              one: ADR-0014 §7.5.3 keeps step 2 done there deliberately, so that steps 3 and 4 can be
              finished and the tenant can read as provisioned while the acceptance is outstanding. The
              activation is said where the operator can act on it — the provisioning section's own badge and
              the nav entry beside it — rather than by reopening a badge the directory shares. */}
          {!provisioningComplete && (
            <span className="badge badge--status-not_started" data-testid="tenant-provisioning">
              {t("tenants.provisioning.badgeIncomplete")}
            </span>
          )}
          {/* Beside "provisiointi kesken" and not instead of it: for a tenant whose step 3 has never been
              done both are true, and they are two different facts — one step is outstanding, and meanwhile
              nobody in that organisation is served. It also stands for a customer whose provisioning IS
              complete and whose applications have since been taken away, which is the case the header had
              no word for. */}
          {shutKey && (
            <span className="badge badge--tenant-frozen" data-testid="tenant-access">
              {t(shutKey)}
            </span>
          )}
        </div>
        <div className="tenant-identity__line tenant-identity__meta">
          <code className="tenants__uuid-value" data-testid="tenant-uuid">
            {tenant.id}
          </code>
          <button
            type="button"
            className="btn btn--ghost btn--small tenants__uuid-copy"
            data-testid="tenant-copy-uuid"
            aria-label={t("tenants.copyUuidLabel", { tenant: tenant.slug })}
            onClick={() => copyToClipboard(tenant.id, onCopied)}
          >
            {t("tenants.copyUuid")}
          </button>
          <span className="tenant-identity__created" data-testid="tenant-created">
            {t("tenants.createdOn", { date: formatDate(new Date(tenant.createdAt)) })}
          </span>
        </div>
      </div>
    </Card>
  );
}
